import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  matchPath,
  pickQueryParams,
  sendApplicationError,
  sendSystemError,
  createCustomError
} from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';

const WEB3_API_URL = import.meta.env.VITE_WEB3_API_URL;

export const web3APIClient = axios.create({
  baseURL: `${WEB3_API_URL}/`
});
web3APIClient.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  Sentry.addBreadcrumb({
    category: 'HTTPS',
    message: `${request.baseURL}${request.url} Requested`,
    data: {
      request: JSON.stringify(request.data),
      auth: JSON.stringify(request.headers?.Authorization ?? 'None')
    }
  });
  return request;
});
web3APIClient.interceptors.response.use(
  (response) => {
    Sentry.addBreadcrumb({
      category: 'HTTPS',
      message: `${response.config.baseURL}${response.config.url} Response`,
      data: {
        response: JSON.stringify(response.config.data)
      }
    });
    return response;
  },
  (error: AxiosError) => {
    error.name = `${error.config?.url ?? 'unknownURL'} ${error.name}`;
    // クエリパラメーターの抽出
    const queryParams = pickQueryParams(error.config?.url ?? '');
    const extra = {
      HTTPS: {
        url: `${error.config?.baseURL}${error.config?.url}`,
        query: JSON.stringify(queryParams),
        statusCode: error.response?.status,
        request: JSON.stringify(error.request),
        header: JSON.stringify(error.config?.headers ?? '')
      }
    };
    // 4xx のエラーをアプリケーションエラーとして拾う
    if (error.response?.status && error.response?.status < 500) {
      // NOTE: error.config.url の部分はパスパラメーター付きのURLがでてくるようになったらパスパラメーターの部分を置き換えるように設定する必要あり
      sendApplicationError(error, error.config?.url ?? '"unknownURL"', extra);
      return Promise.reject(createCustomError(error));
    }

    // 5xx はアプリケーションエラーではないので別イシューで管理する
    sendSystemError(error, error.config?.url ?? '"unknownURL"', extra);
    return Promise.reject(createCustomError(error));
  }
);

const RESERVE_API_URL = import.meta.env.VITE_RESERVE_API_URL;
const reserveAPIPathList = ['/api/reservation/getreservationlist/:cognitoid/'];

export const reserveAPIClient = axios.create({
  baseURL: `${RESERVE_API_URL}/`
});
reserveAPIClient.interceptors.request.use((request) => {
  Sentry.addBreadcrumb({
    category: 'HTTPS',
    message: `${request.baseURL}${request.url} Requested`,
    data: {
      request: JSON.stringify(request.data),
      auth: JSON.stringify(request.headers?.Authorization ?? 'None')
    }
  });
  return request;
});
reserveAPIClient.interceptors.response.use(
  (response) => {
    Sentry.addBreadcrumb({
      category: 'HTTPS',
      message: `${response.config.baseURL}${response.config.url} Response`,
      data: {
        response: JSON.stringify(response.config.data)
      }
    });
    return response;
  },
  (error) => {
    // pathパラメーター部分の抽出とURLのパスパラメーターの部分を置き換え
    const match = matchPath(error.config.url, reserveAPIPathList);
    error.name = `${match?.matchedPath ?? error.config?.url ?? 'unknownURL'} ${error.name}`;
    // クエリパラメーターの抽出
    const queryParams = pickQueryParams(error.config.url);
    const extra = {
      HTTPS: {
        url: match?.matchedPath ?? error.config.url,
        path: JSON.stringify(match?.params),
        query: JSON.stringify(queryParams),
        statusCode: error.response?.status,
        request: JSON.stringify(error.request)
      }
    };
    // 4xx のエラーをアプリケーションエラーとして拾う
    if (error.response?.status && error.response?.status < 500) {
      sendApplicationError(error, match?.matchedPath ?? error.config.url, extra);
      return Promise.reject(createCustomError(error));
    }

    // 5xx はアプリケーションエラーではないので別イシューで管理する
    sendSystemError(error, error.config.url, extra);
    return Promise.reject(createCustomError(error));
  }
);
